import React, { useEffect, useRef, useState } from "react";
import {
  LoadingWrapper,
  LoadingContainer,
  LoadingContent,
  ImageWrapper,
  DragImageWrapper,
  DragImage,
  LogoWrapper,
  Logo,
  LoaderTextContainer,
  LoaderText,
} from "../styles/components/loader";
import animations from "../animations/animations";
import { COLOURS } from "../styles/variables/colours";
import { BREAKPOINTS } from "../styles/variables/breakpoints";
import { getDeviceInfo } from "../data/utils";

const Loader = ({ content, lenisRef, handleLoaded }) => {
  const [minimumTimePassed, setMinimumTimePassed] = useState(false);
  const [fakeProgress, setFakeProgress] = useState(0);
  const [shouldRender, setShouldRender] = useState(true);

  const wrapperRef = useRef(null);
  const loadingContent = useRef(null);
  const imageRefs = useRef([null, null]);
  const loaderTextRef = useRef(null);
  const mmRef = useRef(null);

  const minimumTime = 5000;
  const loaderContent = content[0]?.content ?? [];
  const { isHSWeb, isMobile } = getDeviceInfo();

  const toggleScroll = state => {
    const element =
      document.querySelector("#hs-web") || document.querySelector("#hs-app");
    if (element) {
      element.style.overflow = state;
    } else {
      document.body.style.overflow = state;
    }
  };

  const loaderImages = loaderContent.filter(
    item => item?.moduleTitle === "component--loader-images"
  );

  const loaderLogo = loaderContent.filter(
    item => item?.moduleTitle === "component--loader-logo"
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (isMobile) {
      toggleScroll("hidden");
    } else {
      lenisRef?.current?.stop();
    }
  }, [lenisRef, isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    mmRef.current = animations.matchMedia();

    return () => {
      mmRef.current?.revert();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.scrollTo(0, 0);

    animations.set(loadingContent.current, {
      opacity: 1,
    });
    const timer = setTimeout(() => setMinimumTimePassed(true), minimumTime);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const tridentContainer = document.querySelectorAll(".trident-container");
    tridentContainer.forEach(container => {
      container.classList.add("active");
    });

    const sections = imageRefs.current;
    const left = sections[0];
    const right = sections[1];

    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const scaleWrap = animations.utils.wrap(0, 1);

    mmRef.current.add(`(min-width: ${BREAKPOINTS.md}px)`, () => {
      animations.set(wrapperRef.current, {
        backgroundColor: COLOURS.primary.black,
      });

      sections.forEach((section, index) => {
        animations.set(section, {
          transformOrigin: index === 0 ? "center left" : "center right",
          scaleX: index === 0 ? 0.6 : 0.4,
          scaleY: index === 0 ? 0.8 : 0.3,
        });
      });

      const handleMove = event => {
        const { x: currentX } = event;
        const width = window.innerWidth;
        const clampedX = clamp(scaleWrap(currentX / width), 0.1, 0.9);

        const colour = animations.utils.interpolate(
          COLOURS.tertiary.orange,
          COLOURS.tertiary.green,
          clampedX
        );

        animations.to(wrapperRef.current, {
          backgroundColor: colour,
          duration: 0.2,
          ease: "power2.out",
        });

        animations
          .timeline()
          .to(
            left,
            {
              scaleX: clampedX,
              scaleY: clampedX + 0.2,
              transformOrigin: "center left",
              duration: 0.1,
            },
            0
          )
          .to(
            right,
            {
              scaleX: 1 - clampedX,
              scaleY: 1.3 - clampedX,
              transformOrigin: "center right",
              duration: 0.1,
            },
            0
          );
      };

      animations.Observer.create({
        target: wrapperRef.current,
        onDrag: event => {
          if (event.isDragging) handleMove(event);
          loadingContent.current.classList.add("grabbing");
        },
        onDragEnd: () => {
          loadingContent.current.classList.remove("grabbing");
        },
        onMove: event => {
          if (!event.isDragging) handleMove(event);
        },
      });

      return () => {
        animations.set(wrapperRef.current, { backgroundColor: null });
        sections.forEach(section =>
          animations.set(section, {
            clearProps: "transform, opacity, backgroundColor",
          })
        );
      };
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    mmRef.current.add(`(max-width: ${BREAKPOINTS.md - 1}px)`, () => {
      animations.fromTo(
        wrapperRef.current,
        { backgroundColor: COLOURS.tertiary.orange },
        {
          backgroundColor: COLOURS.tertiary.green,
          duration: 8.0,
          ease: "power2.out",
        }
      );

      let progress = 0;

      const animateCount = () => {
        progress += (100 - progress) * 0.025;

        if (progress >= 99.5) {
          setFakeProgress(100);

          animations
            .timeline({
              onStart: () => {
                handleLoaded(true);
              },
              onComplete: () => {
                toggleScroll("unset");
                setTimeout(() => setShouldRender(false), 200);
              },
            })
            .to(
              loaderTextRef.current,
              {
                delay: 0.5,
                yPercent: 100,
                opacity: 0,
                duration: 2,
              },
              0
            )
            .to(
              wrapperRef.current,
              {
                delay: 0.5,
                opacity: 0,
                duration: 2,
              },
              0
            );

          return;
        }

        setFakeProgress(Math.floor(progress));

        requestAnimationFrame(animateCount);
      };

      setTimeout(() => {
        requestAnimationFrame(animateCount);
      }, 0);
    });
  }, []);

  useEffect(() => {
    if (!minimumTimePassed) return;

    const sections = imageRefs.current;
    const left = sections[0];
    const right = sections[1];

    mmRef.current.add(`(min-width: ${BREAKPOINTS.md}px)`, () => {
      animations
        .timeline({
          onStart: () => {
            handleLoaded(true);
          },
          onComplete: () => {
            setTimeout(() => {
              lenisRef.current?.start?.();
            }, 50);
            setTimeout(() => setShouldRender(false), 200);
          },
        })
        .to(
          left,
          {
            transformOrigin: "center left",
            rotationX: -95,
            perspective: 500,
            yPercent: -50,
            duration: 1,
          },
          0
        )
        .to(
          right,
          {
            transformOrigin: "center right",
            rotationX: -95,
            perspective: 500,
            yPercent: -50,
            duration: 1,
          },
          0
        )
        .to(
          wrapperRef.current,
          {
            scaleY: 0.1,
            height: 0,
            transformOrigin: "top top",
            duration: 1.2,
          },
          0
        );
    });
  }, [minimumTimePassed, lenisRef]);

  return shouldRender ? (
    <LoadingWrapper ref={wrapperRef}>
      <LoadingContainer $isHSWeb={isHSWeb}>
        {loaderLogo[0]?.images?.map((item, index) => {
          const image = item?.image?.fluid?.src;
          return (
            <LogoWrapper key={item.key || index}>
              <Logo src={image} />
            </LogoWrapper>
          );
        })}
        <LoadingContent ref={loadingContent}>
          {loaderImages[0]?.images?.map((item, index) => {
            const image = item?.image?.fluid?.src;
            return (
              <ImageWrapper
                key={item.key || index}
                ref={ref => (imageRefs.current[index] = ref)}
              >
                <DragImageWrapper>
                  <DragImage src={image} />
                </DragImageWrapper>
              </ImageWrapper>
            );
          })}
          <LoaderTextContainer ref={loaderTextRef}>
            <LoaderText> {fakeProgress}%</LoaderText>
          </LoaderTextContainer>
        </LoadingContent>
      </LoadingContainer>
    </LoadingWrapper>
  ) : null;
};

export default Loader;
